import { useMemo, useRef, useEffect } from "react";
import GridContent from "./designer-grid/GridContent";
import { useToast } from "@/hooks/use-toast";
import { useAllProfiles } from "@/v2/hooks/profiles/useAllProfiles";
import { DISCIPLINES } from "@/data/constants";
import { ALL_COUNTRIES } from "@/data/countries";
import SearchSection from "./filters/SearchSection";
import { Profile } from "@/hooks/designer-grid/types";
import { ContentPagination } from "@/v2/services/types/serviceInterfaces";
import { useIsMobile } from '@/hooks/use-mobile';

const DesignerGrid = () => {
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const {
    data,
    isLoading,
    isError,
    handlePageChange,
    refetch,
    filters: {
      selectedLocations,
      selectedDisciplines,
      handleLocationToggle,
      handleDisciplineToggle,
      handleFilterReset,
    },
  } = useAllProfiles();

  // Listen for refresh events
  useEffect(() => {
    const handleRefresh = () => {
      // Reset to page 1 and refetch
      handlePageChange(1);
      refetch();
      // Scroll to top smoothly
      window.scrollTo({ top: 0, behavior: "smooth" });
    };

    window.addEventListener('refresh', handleRefresh);
    return () => {
      window.removeEventListener('refresh', handleRefresh);
    };
  }, [refetch, handlePageChange]);

  const handlePaginationChange = (page: number) => {
    handlePageChange(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleAllReset = () => {
    handleFilterReset();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Ensure we always have a valid array of profiles
  const profiles: Profile[] = (data?.profiles || []) as Profile[];

  // Ensure pagination has all required fields with proper types
  const pagination: ContentPagination = {
    currentPage: data?.pagination?.currentPage || 1,
    totalPages: data?.pagination?.totalPages || 1,
    hasNextPage: !!data?.pagination?.hasNextPage,
    hasPrevPage: !!data?.pagination?.hasPrevPage,
    totalCount: data?.pagination?.totalCount || 0,
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <SearchSection
        selectedDisciplines={selectedDisciplines}
        selectedLocations={selectedLocations}
        onDisciplineToggle={handleDisciplineToggle}
        onLocationToggle={handleLocationToggle}
        onResetFilters={handleAllReset}
        disciplines={DISCIPLINES}
        locations={ALL_COUNTRIES}
      />
      <GridContent
        profiles={profiles}
        loading={isLoading}
        error={isError ? "Something went wrong" : null}
        pagination={pagination}
        hasFilters={
          selectedLocations.length > 0 || selectedDisciplines.length > 0
        }
        category={"trending"}
        onRetry={() => console.log("On Retry")}
        resetFilters={handleAllReset}
        onPageChange={handlePaginationChange}
      />
    </div>
  );
};

export default DesignerGrid;
